
/**
 * @typedef FiltersAutocompleteOption
 * @property {string=} id
 * @property {string} description
 * @property {boolean=} notValid
 */

/**
 * @typedef KeyLabelObject
 * @property {string} key
 * @property {string} label
 */

/**
 * Filters used in the Risk&Crisis Identification tab
 *
 * @enum {KeyLabelObject}
 */
export const RCFilterType = {
    ZONE: {key: 'zone', label: 'Zone'},
    MARKET: {key: 'market', label: 'Market'},
    PLANT: {key: 'plants', label: 'Plant'},
    MATERIAL: {key: 'material', label: 'Material (s)'},
    SPEND_CAT_L1: {key: 'spendCategory1', label: 'Spend Cat L1'},
    SPEND_CAT_L2: {key: 'spendCategory2', label: 'Spend Cat L2'},
    SPEND_CAT_L3: {key: 'spendCategory3', label: 'Spend Cat L3'},
    SPECIFICATION: {key: 'specification', label: 'Specification'},
    MAIN_VENDOR: {key: 'mainVendor', label: 'Main vendor'},
    VENDOR_SITE: {key: 'vendorSiteNumber', label: 'Vendor site'},
    VENDOR_SITE_CITY: {key: 'vendorSiteCity', label: 'Vendor site city'},
    VENDOR_SITE_COUNTRY: {key: 'vendorSiteCountry', label: 'Vendor site country'},
    FINANCIAL_RATING: {key: 'financialRating', label: 'Financial rating'},
    BCP_STATUS: {key: 'reasonCode', label: 'BCP Status'},
};

/**
 * Filters used in the Sourcing Flexibility, Reason Code Input tab
 * @enum {KeyLabelObject}
 */
export const SFFilterType = {
    PLANT: {key: 'plants', label: 'Market / Plant *'},
    MATERIAL: {key: 'material', label: 'Material'},
    MATERIAL_TYPE: {key: 'materialType', label: 'Material Type'},
    SPECIFICATION: {key: 'specification', label: 'Specification'},
    REASON_CODE: {key: 'reasonCode', label: 'Reason Code'},
    MODIFIED_BY: {key: 'modified-by', label: 'Modified By'},
    MATERIAL_GROUP: {key: 'materialGroup', label: 'Material Group'},
    SPEND_CAT_L1: {key: 'spendCategory1', label: 'Category'},
    NNS: {key: 'nns', label: 'NNS'},
    PS_NAME: {key: 'psName', label: 'PS Name'},
    PM_NAME: {key: 'pmName', label: 'PM Name'},
    TASTY_CLASSIFICATION: {key: 'tastyClassification', label: 'Tasty Classification'},
};

export const addLeadingZeroes = (material) => {
    return material.padStart(18, "0")
}

/**
 * From an array of filter option objects (as received from the API lookup endpoint)
 * Format it into an array of string that is taken as input for API lookup, and material, endpoints
 *  + Invalid options are removed.
 * Note: Adapted from updateSelectedArray function
 *
 * @param {string} type
 * @param {Array<FiltersAutocompleteOption>} options
 * @return {Array<string>}
 */
export const formatFilterOptionsForAPI = (type, options) => {
    let mapFunction;
    switch (type) {
        case 'material':
            mapFunction = (option) => addLeadingZeroes(option.id);
            break;
        case 'plant': // section_sku Implementation
            mapFunction = (option) => option.plantID;
            break;
        case 'plants': // R&C implementation
            mapFunction = (option) => option.id;
            break;
        case 'modified-by': // in SF app, get the userID between the parentheses
            mapFunction = (option) => option.description.split('(').pop().split(')')[0];
            break;
        case 'specification':
            mapFunction = (option) => option.id;
            break;
        case 'reasonCode': // in SF app it is the id, in R&C it is the description
            mapFunction = (option) => option.id || option.description;
            break;
        case 'nestecProductCategory':
            // TODO implement based on updateSelectedArray when using for SF app
            mapFunction = (option) => option.id;
            break;
        case 'mainVendor':
        case 'vendorSiteNumber':
            mapFunction = (option) => option.id;
            break;
        case 'nns':
            mapFunction = (option) => {
                return option.description;
            }
            break;
        default:
            mapFunction = (option) => option.description.replace("'", "''");
            break;
    }
    return options
        .filter((opt) => opt.notValid !== true)
        .map(mapFunction);
};

/**
 * Expand selected values in an autocomplete with values that have been pasted by the user,
 * testing if each of the values is existing in the available options
 *  + mark options not matching anything as invalid
 *  + Avoid adding 2 times the same item tot the selection
 *
 * @param {string} pastedText
 * @param {Array<FiltersAutocompleteOption>} options
 * @param {Array<FiltersAutocompleteOption>} selectedOptions
 * @return {Array<FiltersAutocompleteOption>}
 */
export const expandSelectionWithPastedText = (pastedText, options, selectedOptions) => {
    const pastedValues = new Set(pastedText.split(';').map((v) => v.trim()));
    pastedValues.delete('');

    const getDescriptionOrId = (opt) => (parseInt(opt.id) && opt.id.length > 4) ? parseInt(opt.id).toString() : opt.description;

    const valuesToAdd = [];
    options.forEach((opt) => {
        const descOrId = getDescriptionOrId(opt);
        if (pastedValues.has(descOrId)) {
            pastedValues.delete(descOrId);
            if (selectedOptions.find((opt) => getDescriptionOrId(opt) === descOrId) === undefined) {
                valuesToAdd.push({
                    ...opt,
                    notValid: false,
                });
            }
        }
    });
    const notMatching = [...pastedValues].map((v) => ({description: v, notValid: true}));
    return [...notMatching, ...selectedOptions, ...valuesToAdd];
};
